import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { LoginRequest } from '../../../models/user.model';
import { RoleId } from '../../../constants/user-roles.constants';

@Component({
  selector: 'app-login',
  imports: [ReactiveFormsModule, RouterModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  isLoading = false;
  errorMessage = '';
  showPassword = false;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6)]],
      rememberMe: [false]
    });
  }

  ngOnInit(): void {
    // Check if user is already logged in and redirect to appropriate dashboard
    if (this.authService.isAuthenticated()) {
      this.redirectToDashboard();
    }
  }

  private redirectToDashboard(): void {
    const user = this.authService.getCurrentUser();
    if (!user) {
      // If no user, redirect to customer dashboard as default
      this.router.navigate(['/customer']);
      return;
    }

    // Ensure roleId is a number for comparison (handle both string and number)
    const roleId = typeof user.roleId === 'string' ? parseInt(user.roleId, 10) : Number(user.roleId);
    
    if (roleId === RoleId.SuperAdmin || roleId === RoleId.Admin) {
      this.router.navigate(['/admin']);
    } else if (roleId === RoleId.Dispatcher) {
      this.router.navigate(['/dispatcher']);
    } else if (roleId === RoleId.Driver) {
      this.router.navigate(['/driver']);
    } else if (roleId === RoleId.User) {
      this.router.navigate(['/customer']);
    } else {
      // Default to customer dashboard instead of landing page
      this.router.navigate(['/customer']);
    }
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';

      const loginRequest: LoginRequest = {
        email: this.loginForm.value.email,
        password: this.loginForm.value.password,
        rememberMe: this.loginForm.value.rememberMe
      };

      this.authService.login(loginRequest).subscribe({
        next: (response) => {
          this.isLoading = false;
          // Redirect to appropriate dashboard based on user roleId
          this.redirectToDashboard();
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = error.error?.message || error.error?.error || 'Login failed. Please check your credentials and try again.';
        }
      });
    } else {
      this.markFormGroupTouched();
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.loginForm.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  private markFormGroupTouched(): void {
    Object.keys(this.loginForm.controls).forEach(key => {
      const control = this.loginForm.get(key);
      control?.markAsTouched();
    });
  }
}